import { FC, useCallback, useEffect, useMemo, useState } from "react";
import {
  ColorValue,
  NativeSyntheticEvent,
  StyleSheet,
  Text,
  TextInput,
  TextInputKeyPressEventData,
  TouchableOpacity,
  View,
  ViewStyle,
} from "react-native";
import unionBy from "lodash/unionBy";

import { kColors } from "src/kColors";
import { kFonts } from "src/kFonts";
import { kStyleConst } from "src/kStyle";
import { DeleteIcon } from "./impComponents";

type tTagWrapper = {
  value: string;
  isValid: boolean;
};

const checkIfValid = (value: string, pattern: RegExp) => {
  const kMatchResults = value.match(pattern);
  if (kMatchResults === null || kMatchResults.length === 0) {
    return false;
  }
  return true;
};

const checkIfExists = (value: string, tags: Array<tTagWrapper>) => {
  let aExists = false;
  tags.forEach((myTag) => {
    if (myTag.value === value) aExists = true;
  });
  return aExists;
};

type tEmitValidityState = ({
  onValidityStateChange,
  validationPattern,
  tags,
}: {
  onValidityStateChange?: (state: boolean) => void;
  validationPattern?: RegExp;
  tags: Array<tTagWrapper>;
}) => void;
const emitValidityState: tEmitValidityState = ({ onValidityStateChange, validationPattern, tags }) => {
  if (validationPattern && onValidityStateChange) {
    let aIsValid = true;
    tags.forEach((myTag) => {
      if (!myTag.isValid) aIsValid = false;
    });
    onValidityStateChange(aIsValid);
  }
};

interface tTagsInputProps {
  noSpace?: boolean;
  onChangeTags?: (myTags: Array<string>) => void;
  value?: string;
  placeholder?: string;
  placeholderTextColor?: ColorValue;
  highlightStyle?: ViewStyle;
  deleteInvalidStyle?: ViewStyle;
  invalidStyle?: ViewStyle;
  deleteStyle?: ViewStyle;
  inputStyleNoTags?: ViewStyle;
  inputStyleWithTags?: ViewStyle;
  validationPattern?: RegExp;
  onValidityStateChange?: (state: boolean) => void;
  limitTo?: number;
  allowSpace?: boolean;
}

const TagsInput: FC<tTagsInputProps> = ({
  noSpace = false,
  onChangeTags,
  value,
  placeholder,
  placeholderTextColor,
  deleteInvalidStyle = styles.tagDeleteInvalid,
  invalidStyle = styles.tagInvalid,
  deleteStyle = styles.tagHighlighted,
  highlightStyle = styles.tag,
  inputStyleNoTags = styles.tagsTextInput,
  inputStyleWithTags = styles.tagsTextInput,
  validationPattern,
  onValidityStateChange,
  limitTo,
  allowSpace,
}) => {
  const [sTags, _setTags] = useState<Array<tTagWrapper>>([]);
  const [sNewTag, _setNewTag] = useState<string>("");
  const [sConfirmDeleteIndex, _setConfirmDeleteIndex] = useState<number | null>(null);

  const canAdd = useMemo(() => (limitTo ? limitTo > sTags.length : true), [limitTo, sTags.length]);

  useEffect(() => {
    if (value) {
      const aNewOriginalValues = value.split(",");
      _setTags(aNewOriginalValues.map((myVal) => ({ value: myVal.trim(), isValid: true })));
    } else {
      _setTags([]);
    }
  }, [value]);

  const checkAndAdd = (tags: string[]) => {
    const newTags = unionBy(
      sTags,
      tags.map((tag) => ({
        value: tag.trim(),
        isValid: validationPattern ? checkIfValid(tag.trim(), validationPattern) : true,
      })),
      (tag) => tag.value,
    );
    const limitedTags = limitTo ? newTags.splice(0, limitTo) : newTags;
    if (onChangeTags) {
      onChangeTags(limitedTags.map((myTag) => myTag.value));
    }
    _setTags(limitedTags);
    _setNewTag("");
    emitValidityState({ onValidityStateChange, validationPattern, tags: limitedTags });
  };

  const _handleAddTag = () => {
    const maybeList = sNewTag.split(allowSpace ? "," : /[, ]+/);
    if (maybeList.length > 0) checkAndAdd(maybeList.filter((tag) => tag.length > 0));
  };

  const _handleKeyPress = (myEvent: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
    if (
      myEvent.nativeEvent.key === "," ||
      (myEvent.nativeEvent.key === " " && noSpace) ||
      myEvent.nativeEvent.key === "Enter"
    ) {
      _handleAddTag();
      myEvent.preventDefault();
    } else if (myEvent.nativeEvent.key === "Backspace" && sNewTag === "") {
      if (sConfirmDeleteIndex !== null) {
        _setConfirmDeleteIndex(sTags.length - 2);
        const removeTag = [...sTags].filter((_, myIndex) => myIndex !== sConfirmDeleteIndex);
        _setTags(removeTag);
        if (onChangeTags) onChangeTags(removeTag.map((myTag) => myTag.value));
      } else {
        _setConfirmDeleteIndex(sTags.length - 1);
      }
    } else {
      if (sConfirmDeleteIndex !== null) _setConfirmDeleteIndex(null);
    }
  };

  const _handleOnBlur = () => {
    if (sNewTag !== "") {
      _handleAddTag();
    }
  };

  const _handleOnPress = (myIndex: number) => {
    if (sConfirmDeleteIndex === myIndex) {
      let removeTag = [...sTags].filter((_, myFilterIndex) => myFilterIndex !== sConfirmDeleteIndex);
      removeTag = removeTag.map((myTag, myTagIndex) => ({
        value: myTag.value,
        isValid:
          (!validationPattern || checkIfValid(myTag.value, validationPattern)) &&
          !checkIfExists(myTag.value, removeTag.slice(0, myTagIndex)),
      }));
      _setTags(removeTag);
      _setConfirmDeleteIndex(null);
      emitValidityState({ onValidityStateChange, validationPattern, tags: removeTag });
    }
    _setConfirmDeleteIndex(myIndex);
  };

  return (
    <View style={[sTags.length === 0 ? inputStyleNoTags : inputStyleWithTags, styles.tagsInput]}>
      {sTags.map((myTag, myIndex) => (
        <Tag
          myTag={myTag}
          myIndex={myIndex}
          onPress={_handleOnPress}
          myConfirmDeleteIndex={sConfirmDeleteIndex}
          deleteInvalidStyle={deleteInvalidStyle}
          invalidStyle={invalidStyle}
          deleteStyle={deleteStyle}
          highlightStyle={highlightStyle}
          key={myTag.value}
        />
      ))}
      <TextInput
        placeholder={sTags.length === 0 ? placeholder : undefined}
        placeholderTextColor={placeholderTextColor}
        onBlur={_handleOnBlur}
        style={styles.tagsTextInput}
        value={sNewTag}
        onChangeText={_setNewTag}
        onSubmitEditing={_handleAddTag}
        blurOnSubmit={false}
        onKeyPress={_handleKeyPress}
        autoCorrect={false}
        editable={canAdd}
      />
    </View>
  );
};

type tTag = {
  myTag: tTagWrapper;
  myIndex: number;
  onPress: (myIndex: number) => void;
  myConfirmDeleteIndex: number | null;
  deleteInvalidStyle: ViewStyle;
  invalidStyle: ViewStyle;
  deleteStyle: ViewStyle;
  highlightStyle: ViewStyle;
};
const Tag: FC<tTag> = ({
  myTag,
  myIndex,
  onPress,
  myConfirmDeleteIndex,
  deleteStyle,
  highlightStyle,
  deleteInvalidStyle,
  invalidStyle,
}) => {
  const _handleOnPress = useCallback(() => {
    onPress(myIndex);
  }, [myIndex, onPress]);
  return (
    <TouchableOpacity
      style={
        myIndex === myConfirmDeleteIndex && !myTag.isValid
          ? [deleteInvalidStyle]
          : !myTag.isValid
          ? [invalidStyle]
          : myIndex === myConfirmDeleteIndex
          ? [deleteStyle]
          : [highlightStyle]
      }
      onPress={_handleOnPress}
    >
      <Text key={`${myTag.toString()}${myIndex}`} style={myTag.isValid ? null : styles.textWhite}>
        {myTag.value}
      </Text>
      {myConfirmDeleteIndex === myIndex && (
        <View style={styles.tagDeleteView}>
          <DeleteIcon color={myTag.isValid ? undefined : "white"} />
        </View>
      )}
    </TouchableOpacity>
  );
};

export default TagsInput;

const styles = StyleSheet.create({
  tag: {
    alignItems: "center",
    backgroundColor: kColors.almostWhite,
    borderColor: kColors.lightBlack,
    borderRadius: 999,
    borderWidth: 1,
    flexDirection: "row",
    marginRight: 5,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  tagDeleteInvalid: {
    alignItems: "center",
    backgroundColor: kColors.red,
    borderColor: kColors.lightBlack,
    borderRadius: 999,
    borderWidth: 1,
    flexDirection: "row",
    marginRight: 5,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  tagDeleteView: {
    ...kStyleConst.kTagDeleteIconDimensions,
    marginStart: kStyleConst.kSpacing,
  },
  tagHighlighted: {
    alignItems: "center",
    backgroundColor: kColors.lightBlack,
    borderColor: kColors.lightBlack,
    borderRadius: 999,
    borderWidth: 1,
    flexDirection: "row",
    marginRight: 5,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  tagInvalid: {
    alignItems: "center",
    backgroundColor: kColors.red,
    borderColor: kColors.lightBlack,
    borderRadius: 999,
    borderWidth: 1,
    flexDirection: "row",
    marginRight: 5,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  tagsInput: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  tagsTextInput: {
    ...kFonts.poppinsRegularMedium,
    flex: 1,
    letterSpacing: 1,
  },
  textWhite: {
    color: kColors.white,
  },
});
